<template>
    <div>
        <div id="create-blacklist-phone-pane" v-mdl class="mdl-card mdl-shadow--6dp">
            <div class="mdl-card__title">
                <h2 class="mdl-card__title-text">
                    {{ $t('blacklist.create_phone') }}
                </h2>
            </div>
            <div class="mdl-card__supporting-text">
                <form>
                    <div class="mdl-textfield mdl-js-textfield">
                        <input id="phone" v-model="phone" class="mdl-textfield__input" autofocus>
                        <label class="mdl-textfield__label" for="phone">{{ $t('blacklist.phone') }}</label>
                    </div>
                </form>
            </div>
            <!-- PULS-1383: Android asks with three buttons (block text, block sender, cancel); the
                 dialog library only has two, so the choice is shown in the card instead. -->
            <div v-if="ambiguousEntry" class="mdl-card__supporting-text ambiguous-message">
                {{ $t('blacklist.ambiguous', { entry: ambiguousEntry }) }}
            </div>
            <div v-if="ambiguousEntry" class="mdl-card__actions mdl-card--border">
                <button id="block-text" class="mdl-button mdl-js-button mdl-js-ripple-effect" @click="resolveAmbiguous('phrase')">
                    {{ $t('blacklist.block_text') }}
                </button>
                <button id="block-sender" class="mdl-button mdl-js-button mdl-js-ripple-effect" @click="resolveAmbiguous('phone_number')">
                    {{ $t('blacklist.block_sender') }}
                </button>
                <button id="cancel-ambiguous" class="mdl-button mdl-js-button mdl-js-ripple-effect" @click="ambiguousEntry = null">
                    {{ $t('dialog.cancel') }}
                </button>
            </div>
            <div v-else class="mdl-card__actions mdl-card--border">
                <button id="create-phone" class="mdl-button mdl-js-button mdl-js-ripple-effect" @click="createPhone">
                    {{ $t('dialog.create') }}
                </button>
                <button id="cancel" class="mdl-button mdl-js-button mdl-js-ripple-effect" @click="cancel">
                    {{ $t('dialog.cancel') }}
                </button>
            </div>


            <transition name="loading-fade">
                <div v-if="loading" class="loading-center">
                    <spinner />
                </div>
            </transition>
        </div>

        <div id="create-blacklist-phrase-pane" v-mdl class="mdl-card mdl-shadow--6dp">
            <div class="mdl-card__title">
                <h2 class="mdl-card__title-text">
                    {{ $t('blacklist.create_phrase') }}
                </h2>
            </div>
            <div class="mdl-card__supporting-text">
                <form>
                    <div class="mdl-textfield mdl-js-textfield">
                        <input id="phrase" v-model="phrase" class="mdl-textfield__input" autofocus>
                        <label class="mdl-textfield__label" for="phrase">{{ $t('blacklist.phrase') }}</label>
                    </div>
                </form>
            </div>
            <div class="mdl-card__actions mdl-card--border">
                <button id="create-phrase" class="mdl-button mdl-js-button mdl-js-ripple-effect" @click="createPhrase">
                    {{ $t('dialog.create') }}
                </button>
                <button id="cancel" class="mdl-button mdl-js-button mdl-js-ripple-effect" @click="cancel">
                    {{ $t('dialog.cancel') }}
                </button>
            </div>


            <transition name="loading-fade">
                <div v-if="loading" class="loading-center">
                    <spinner />
                </div>
            </transition>
        </div>
    </div>
</template>

<script>

import { Api, SessionCache, Util } from '@/utils/';
import { findEquivalentEntry, isPhraseLike } from '@/utils/blocklist.js';
import Spinner from '@/components/Spinner.vue';

export default {
    name: 'CreateBlacklist',
    components: {
        Spinner
    },
    data () {
        return {
            title: "",
            phone: "",
            phrase: "",
            ambiguousEntry: null,
            loading: false,
        };
    },
    watch: {
        // Editing the number drops a pending "text or sender?" question about the old text.
        phone () {
            this.ambiguousEntry = null;
        }
    },
    mounted () {
        this.$store.commit("loading", false);
        this.$store.commit('title', this.title);
    },
    methods: {
        createPhone () {
            const phone = this.phone.trim();
            if (phone === '')
                return;

            // PULS-1383: text like "Fashion Nova" typed here would be saved as a phone number and
            // never match anything, so ask which one was meant, as Android does.
            if (!isPhraseLike(phone)) {
                this.add({ phone_number: phone });
                return;
            }

            this.ambiguousEntry = phone;
        },

        // type is 'phrase' or 'phone_number'.
        resolveAmbiguous (type) {
            const entry = this.ambiguousEntry;
            this.ambiguousEntry = null;
            this.add({ [type]: entry });
        },

        createPhrase () {
            const phrase = this.phrase.trim();
            if (phrase === '')
                return;

            this.add({ phrase });
        },

        // PULS-1405: don't add a second entry that blocks the same thing; say so instead.
        add (entry) {
            this.loading = true;

            this.loadBlocklist()
                .then(entries => {
                    if (findEquivalentEntry(entries, entry)) {
                        this.loading = false;
                        const options = { okText: this.$t('dialog.close'), animation: 'fade' };
                        this.$dialog.alert(this.$t('blacklist.already_blocked', { entry: entry.phone_number || entry.phrase }), options)
                            .catch(() => { });
                        return;
                    }

                    const request = entry.phone_number
                        ? Api.blacklist.create.phone(entry.phone_number)
                        : Api.blacklist.create.phrase(entry.phrase);
                    request
                        .then(() => this.handleCreated())
                        .catch(() => {
                            this.loading = false;
                            Util.snackbar(this.$t('blacklist.create_failed'));
                        });
                });
        },

        // The list page fills the cache; fetch it if this page was opened directly.
        loadBlocklist () {
            const cached = SessionCache.getBlacklists();
            if (cached.length > 0) {
                return Promise.resolve(cached);
            }

            return Api.blacklist.get().catch(() => []);
        },

        handleCreated () {
            this.loading = false;
            this.$router.push({ name: 'blacklists'});
        },

        cancel () {
            this.$router.push({ name: 'blacklists'});
        }
    }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
    @import "../../assets/scss/_vars.scss";

    #create-blacklist-phone-pane {
        position: relative;
        width: 330px;
        height: 100%;
        margin: 5em auto;
    }

    .ambiguous-message {
        padding-top: 0;
    }

    #create-blacklist-phrase-pane {
        position: relative;
        width: 330px;
        height: 100%;
        margin: 5em auto;
    }

    .loading-center {
        position: absolute;
        margin: 57px auto;
        background: #fff;

        height: 100%;
        width: 100%;

        text-align: center;
        vertical-align: middle;

        .spinner {
            margin: 35% auto;
            translate: scale(2);
        }
    }

    /* loading-fade transition */
    .loading-fade-enter-active {
        transition-delay: 1s;
        transition: all $anim-time ease;
    }
    .loading-fade-leave-active {
        transition-delay: 1s;
        transition: all $anim-time ease;
    }
    .loading-fade-enter, .loading-fade-leave-to {
        transform: translateY(70%);
        opacity: 0;
    }

</style>
